import React, {useCallback, useEffect, useRef, useState} from 'react';
import {watchScene} from '../sceneRepository';
import {isSceneLive, isSceneShownTo, sceneShowKey, shouldPlaySceneIntro} from '../scene';
import {vtmT} from '../i18n';

const INTRO_MS = 4200;

/** Écoute l'image montrée par le MJ pour une campagne (undefined tant que rien n'est reçu). */
export const useScene = (campaignUid) => {
  const [scene, setScene] = useState(undefined);
  useEffect(() => (campaignUid ? watchScene(campaignUid, setScene) : undefined), [campaignUid]);
  return scene;
};

/**
 * Image du MJ : glisse depuis la droite au-dessus du chat, la fiche reste visible à gauche.
 * Réductible en un bandeau ; réaffichée à chaque nouvelle image.
 * @param {Function} [props.onStop] côté MJ : retirer l'image pour tout le monde
 */
export const VtmScenePanel = ({url, caption, onStop}) => {
  const [minimized, setMinimized] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => setMinimized(false), [url]);

  useEffect(() => {
    if (!zoomed) return undefined;
    const onKey = event => event.key === 'Escape' && setZoomed(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed]);

  if (minimized) {
    return (
      <button type="button" className="vtmSceneTab" onClick={() => setMinimized(false)}>
        <span className="vtmSceneDot" aria-hidden="true" />
        {vtmT('sceneReopen')}
      </button>
    );
  }

  return (
    <section className="vtmScenePanel" aria-label={vtmT('sceneTitle')}>
      <div className="vtmSceneHeader">
        <span className="vtmSceneTitle">{caption || vtmT('sceneTitle')}</span>
        <div className="vtmSceneActions">
          {onStop && <button type="button" className="vtmSceneButton danger" onClick={onStop}>{vtmT('sceneStop')}</button>}
          <button type="button" className="vtmSceneButton" onClick={() => setMinimized(true)}>{vtmT('sceneMinimize')}</button>
        </div>
      </div>
      <button type="button" className="vtmSceneImageButton" onClick={() => setZoomed(true)} title={vtmT('sceneZoom')}>
        <img src={url} alt={caption || vtmT('sceneTitle')} className="vtmSceneImage" />
      </button>
      {zoomed && (
        <div className="vtmSceneZoom" role="dialog" aria-label={vtmT('sceneTitle')} onClick={() => setZoomed(false)}>
          <img src={url} alt={vtmT('sceneTitle')} />
        </div>
      )}
    </section>
  );
};

/** Entrée de scène : l'image occupe l'écran avec sa légende, puis rejoint le panneau. Un clic la passe. */
const VtmSceneIntro = ({url, caption, onDone}) => {
  useEffect(() => {
    const timer = setTimeout(onDone, INTRO_MS);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="vtmSceneIntro" role="dialog" aria-label={caption || vtmT('sceneTitle')} onClick={onDone}>
      <img className="vtmSceneIntroImage" src={url} alt="" />
      {caption && (
        <div className="vtmSceneIntroCaption">
          <span className="vtmSceneIntroRule" aria-hidden="true" />
          <span className="vtmSceneIntroText">{caption}</span>
          <span className="vtmSceneIntroRule" aria-hidden="true" />
        </div>
      )}
      <span className="vtmSceneIntroSkip">{vtmT('sceneIntroSkip')}</span>
    </div>
  );
};

/** Joue l'entrée de scène quand le MJ montre (ou remontre) l'image à ce personnage. */
const useSceneIntro = (scene, characterUid) => {
  const [intro, setIntro] = useState(false);
  const previousKey = useRef(undefined);
  const key = scene === undefined ? undefined : sceneShowKey(scene, characterUid);

  useEffect(() => {
    if (key === undefined) return;
    if (shouldPlaySceneIntro(previousKey.current, key)) setIntro(true);
    if (key === null) setIntro(false);
    previousKey.current = key;
  }, [key]);

  const endIntro = useCallback(() => setIntro(false), []);
  return [intro, endIntro];
};

/** Côté joueur : l'image n'apparaît que si le MJ l'a montrée à ce personnage. */
export const VtmSceneForCharacter = ({campaignUid, characterUid}) => {
  const scene = useScene(campaignUid);
  const [intro, endIntro] = useSceneIntro(scene, characterUid);
  if (!isSceneShownTo(scene, characterUid)) return null;
  return (
    <>
      {intro && <VtmSceneIntro key={sceneShowKey(scene, characterUid)} url={scene.url} caption={scene.caption} onDone={endIntro} />}
      <VtmScenePanel url={scene.url} caption={scene.caption} />
    </>
  );
};

/** Côté MJ : l'image en cours, avec de quoi la retirer. */
export const VtmSceneForDm = ({scene, onStop}) => (isSceneLive(scene) ? <VtmScenePanel url={scene.url} caption={scene.caption} onStop={onStop} /> : null);
